import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "bet_size", label: "Bet size", unit: "$", help: "How much the bot spends on each bet.", to: (v) => v, from: (v) => v },
  { key: "take_profit_pct", label: "Take profit", unit: "+%", help: "Sell once the bet is up this much.", to: (v) => v, from: (v) => v },
  { key: "stop_loss_pct", label: "Stop loss", unit: "-%", help: "Sell once the bet is down this much, to limit losses.", to: (v) => Math.abs(v), from: (v) => -Math.abs(v) },
  { key: "exit_before_close_sec", label: "Exit before close", unit: "sec", help: "Cash out this many seconds before the window ends.", to: (v) => v, from: (v) => v },
  { key: "min_edge", label: "Minimum bargain", unit: "pts", help: "How much cheaper than the bot's estimate a side must be (after fees) before it bets. Higher = fewer, pickier bets.", to: (v) => Math.round(v * 100), from: (v) => v / 100 },
  { key: "max_trades_per_window", label: "Max trades / window", unit: "#", help: "How many separate trades the bot may make in one 15-min window (one open at a time).", to: (v) => v, from: (v) => Math.round(v) },
  { key: "daily_loss_limit", label: "Daily loss limit", unit: "$", help: "Bot stops betting for the day after losing this much.", to: (v) => v, from: (v) => v },
];

export const SettingsCard = ({ rules, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rules) setForm(Object.fromEntries(FIELDS.map((f) => [f.key, f.to(rules[f.key])])));
  }, [rules?.bet_size, rules?.daily_loss_limit]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(FIELDS.map((f) => [f.key, f.from(Number(form[f.key]))]));
      await axios.put(`${API}/bot/settings`, body);
      toast.success("Rules saved. The bot uses them from its next check.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Couldn't save rules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="settings-card">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-[#22D3EE]" /> Your rules</h2>
      <p className="text-xs text-slate-400 mt-1">Change how the bot bets and cashes out. Changes apply to new decisions right away.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[10px] uppercase tracking-widest font-num text-slate-400">{f.label} ({f.unit})</span>
            <Input type="number" inputMode="decimal" value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="mt-1 bg-[#05070D] border-[#1A2338] font-num h-11" data-testid={`setting-${f.key}`} />
            <span className="text-[11px] text-slate-500 leading-snug block mt-1">{f.help}</span>
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="settings-save-btn"
        className="mt-4 px-5 py-2.5 rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-[#03141A] text-sm font-semibold transition-colors disabled:opacity-50">
        {saving ? "Saving…" : "Save rules"}
      </button>
    </section>
  );
};
